/**
 * SEO Utilities for dynamic, scalable SEO management
 * 
 * Handles:
 * - Slug generation from product names
 * - Meta tag updates
 * - Canonical URL management
 */

const SITE_URL = 'https://caivonclub.se';
const BRAND_NAME = 'Càivonclub';

/**
 * Generate a URL-friendly slug from a product name (cosmetic only)
 * Handles Swedish, English, and Arabic characters
 */
export const generateSlug = (name: string): string => {
  if (!name) return '';
  
  // Normalize and convert to lowercase
  return name
    .toLowerCase()
    .trim()
    // Replace Swedish characters
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    // Remove Arabic and other non-Latin characters
    .replace(/[^\x00-\x7F]/g, '')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove special characters except hyphens
    .replace(/[^a-z0-9-]/g, '')
    // Remove consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '');
};

/**
 * Build a product URL with deterministic format: /products/{id}/{slug}
 * - {id} is the real product ID from DB (used for O(1) lookup)
 * - {slug} is cosmetic/SEO only
 */
export const buildProductUrl = (product: { id: string; name: { en: string } }): string => {
  const slug = generateSlug(product.name.en) || 'product';
  return `/products/${product.id}/${slug}`;
};

/**
 * Get the full canonical URL for a path
 */
export const getCanonicalUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

/**
 * Update document meta tags dynamically
 */
export const updateMetaTags = ({
  title,
  description,
  canonicalUrl,
  noindex = false,
  ogImage,
  ogType = 'website',
}: {
  title: string;
  description: string;
  canonicalUrl?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'product';
}) => {
  // Update title
  document.title = title;
  
  // Update or create meta description
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute('content', description.slice(0, 160));
  
  // Update or create canonical link
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (canonicalUrl) {
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
  } else if (canonicalLink) {
    canonicalLink.remove();
  }
  
  // Handle robots meta
  let robotsMeta = document.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, follow');
  } else if (robotsMeta) {
    robotsMeta.remove();
  }
  
  // Update Open Graph tags
  updateOgTag('og:title', title);
  updateOgTag('og:description', description.slice(0, 160));
  updateOgTag('og:type', ogType);
  if (ogImage) {
    updateOgTag('og:image', ogImage);
  }
  if (canonicalUrl) {
    updateOgTag('og:url', canonicalUrl);
  }
};

/**
 * Helper to update Open Graph meta tags
 */
const updateOgTag = (property: string, content: string) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

/**
 * Generate JSON-LD Product structured data
 */
export const generateProductSchema = (product: {
  name: string;
  description?: string;
  price: number;
  discountedPrice?: number;
  image: string;
  inStock: boolean;
  url: string;
  brand?: string;
  currency?: string;
}): string => {
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description || '',
    image: product.image.startsWith('http') ? product.image : `${SITE_URL}${product.image}`,
    brand: {
      '@type': 'Brand',
      name: product.brand || BRAND_NAME,
    },
    offers: {
      '@type': 'Offer',
      url: product.url.startsWith('http') ? product.url : `${SITE_URL}${product.url}`,
      priceCurrency: product.currency || 'SEK',
      price: product.discountedPrice || product.price,
      ...(product.discountedPrice && {
        priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }),
      availability: product.inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    },
  };
  
  return JSON.stringify(schema);
};

/**
 * Generate JSON-LD for the organization/website
 * @param seoLogoUrl - Optional custom logo URL from database (should be 512x512 for Google)
 * @param socialLinks - Optional social media links from database
 */
export const generateOrganizationSchema = (options?: {
  seoLogoUrl?: string;
  socialLinks?: string[];
}): string => {
  // Google requires logo to be publicly accessible, min 112x112, prefer 512x512
  const logoUrl = options?.seoLogoUrl 
    ? (options.seoLogoUrl.startsWith('http') ? options.seoLogoUrl : `${SITE_URL}${options.seoLogoUrl}`)
    : `${SITE_URL}/images/seo-logo-512.png`;
    
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND_NAME,
    url: SITE_URL,
    logo: logoUrl,
    description: 'Luxury organic beauty products from Lebanon. 100% natural, handcrafted skincare from Khan El Saboun.',
    sameAs: options?.socialLinks || [
      'https://www.instagram.com/caivonclub',
      'https://www.facebook.com/caivonclub',
    ],
  };
  
  return JSON.stringify(schema);
};

/**
 * Generate JSON-LD BreadcrumbList
 */
export const generateBreadcrumbSchema = (
  breadcrumbs: { name: string; url: string }[]
): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`,
    })),
  };
  
  return JSON.stringify(schema);
};

/**
 * Check if current filters create thin content that should be noindexed
 */
export const isThinContent = (params: {
  search?: string;
  categories?: string[];
  minPrice?: number;
  maxPrice?: number;
  page?: number;
}): boolean => {
  // Noindex if:
  // - Search query is present (creates infinite combinations)
  // - Multiple filters are combined
  // - Price range is heavily restricted
  
  if (params.search && params.search.trim().length > 0) {
    return true;
  }
  
  const filterCount = 
    (params.categories?.length || 0) + 
    (params.minPrice && params.minPrice > 0 ? 1 : 0) + 
    (params.maxPrice && params.maxPrice < 1000 ? 1 : 0);
  
  // If more than 1 filter combination, noindex
  if (filterCount > 1) {
    return true;
  }
  
  return false;
};

/**
 * SEO-related constants
 */
export const SEO_CONFIG = {
  SITE_URL,
  BRAND_NAME,
  DEFAULT_TITLE: `${BRAND_NAME} - Luxury Organic Beauty from Lebanon | Khan El Saboun`,
  DEFAULT_DESCRIPTION: 'Discover 100% organic Lebanese beauty products from Khan El Saboun. Handcrafted soaps, oils, serums with 600+ years of traditional craftsmanship. Shop luxury natural skincare online.',
  DEFAULT_OG_IMAGE: `${SITE_URL}/images/og-default.jpg`,
};
